import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { apiService, isAccessTokenValid, clearExpiredAccessToken, AUTH_SESSION_EXPIRED_EVENT } from './services/api';
import { setRememberEmailEnabled, setRememberedEmail, clearRememberedEmail, clearInflightEmail } from './utils/rememberEmail';
import { selectAwaitingInputJobId } from './utils/csvReviewJob';
import Sidebar from './views/components/Sidebar/Sidebar';
import DataSourceWizard from './views/components/DataSourceWizard/DataSourceWizard';
import DataSources from './views/components/DataSources/DataSources';
import AskResAI from './views/components/AskResAI/AskResAI';
import Automations from './views/components/Automations/Automations';
import PushToBI from './views/components/PushToBI/PushToBI';
import AuditLogs from './views/components/AuditLogs/AuditLogs';
import ActivityLog from './views/components/ActivityLog/ActivityLog';
import Notification from './views/components/Notification/Notification';
import AppHeader from './views/components/AppHeader/AppHeader';
import Login from './views/components/Login/Login';
import Settings from './views/components/Settings/Settings';
import Insights from './views/components/Insights/Insights';
import ContextAuthoring from './views/components/ContextAuthoring/ContextAuthoring';
import Library from './views/components/Library/Library';
import CsvReviewModal from './views/components/CsvReview/CsvReviewModal';
import OnboardingOverlay from './views/components/Onboarding/OnboardingOverlay';
import OrgTimeZonePrompt from './views/components/OrgTimeZonePrompt/OrgTimeZonePrompt';
import { useDataSourceWizardViewModel } from './viewModels/DataSourceWizardViewModel';
import { useCsvReviewViewModel } from './viewModels/CsvReviewViewModel';
import { DataSourceWizardModel } from './models/DataSourceWizardModel';
import { CsvReviewModel } from './models/CsvReviewModel';
import { useLibraryModel } from './viewModels/useLibraryModel';
import { useLibraryPageEnabled } from './utils/useLibraryPageEnabled';
import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react';
import { configureTimestamps } from './shared/datetime/timestamps';

function getInitialAuthState() {
  if (clearExpiredAccessToken()) return false;
  return isAccessTokenValid();
}

// Single source of truth: route path drives what renders. Used for Routes and Sidebar.
const APP_ROUTES = [
  { path: '/', pageId: 'ask-resai', label: 'Ask ResAI', icon: 'chat_bubble', feature: 'can_access_res_ai_page', featureDefault: true },
  { path: '/insights', pageId: 'insights', label: 'Insights', icon: 'dashboard', feature: 'can_access_insights_page', featureDefault: true },
  { path: '/data-sources', pageId: 'data-sources', label: 'Data Sources', icon: 'table_chart', feature: 'can_access_datasets_page' },
  { path: '/automations', pageId: 'automations', label: 'Automations', icon: 'account_tree', feature: 'can_access_automation' },
  { path: '/push-to-bi', pageId: 'push-to-bi', label: 'Push to BI', icon: 'cloud_upload', feature: 'can_access_push_to_bi', featureDefault: true },
  { path: '/activity-log', pageId: 'activity-log', label: 'Activity Log', icon: 'history', featureDefault: true },
  { path: '/audit-logs', pageId: 'audit-logs', label: 'Audit Logs', icon: 'fact_check', feature: 'can_access_audit_logs', featureDefault: true },
  { path: '/settings', pageId: 'settings', label: 'Settings', icon: 'settings', feature: 'can_access_settings' },
  { path: '/context-authoring', pageId: 'context-authoring', label: 'Context Authoring', icon: 'tune', feature: 'can_access_context_authoring', featureDefault: false },
  // Last, so getFirstAccessiblePath never makes Library anyone's landing page while
  // its access is flag-only (no RBAC key yet). The nav order lives in Sidebar.
  { path: '/library', pageId: 'library', label: 'Library', icon: 'folders' },
];
function getPageIdFromPathname(pathname) {
  const normalized = pathname.replace(/\/$/, '') || '/';
  const route = APP_ROUTES.find((r) =>
    r.path === '/' ? normalized === '/' : normalized === r.path || normalized.startsWith(r.path + '/')
  );
  return route?.pageId ?? 'ask-resai';
}

function App() {
  const enabled = useFlag('Test');
  const backupLoginEnabled = useFlag('backup-login');
  const contextPromotionEnabled = useFlag('context-promotion');
  const libraryPageEnabled = useLibraryPageEnabled(); // PRD-1605
  // Parent kill-switch for Insights. The sidebar already hides the entry; this also
  // closes the URL, so the page is absent whatever the per-family widget flags say.
  // Deep-linking the flag-gated page must not bounce before the Unleash client is ready.
  // A fetch failure never marks the client ready, so flagsError is the other way a
  // decision arrives. On a failed fetch the page stays OPEN: the chart family is
  // shipped and resapi keeps serving it through an Unleash outage (its chart gate
  // fails open), so closing the page here would turn a flag-service blip into a
  // feature outage. Only the four unshipped families fail closed, and they do so
  // per card, not per page. (With a warm localStorage bootstrap the client may also
  // report cached values alongside the error; either way the page renders.)
  const { flagsReady, flagsError } = useFlagsStatus();
  const insightsWidgetsEnabled = useFlag('insights_widgets') || flagsError;
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(getInitialAuthState());
  const [me, setMe] = useState(null);

  // Handle Microsoft OAuth login success - check for token in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      localStorage.setItem('access_token', token);
      setIsAuthenticated(true);
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // Reusable profile fetcher — includes X-Active-Org-Id so switched context survives page reloads.
  const fetchUserProfile = useCallback(() => {
    const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    const token = localStorage.getItem('access_token');
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const activeOrgId = localStorage.getItem('active_org_id');
    if (activeOrgId) headers['X-Active-Org-Id'] = activeOrgId;
    fetch(`${apiBase}/api/v1/user-profile`, { headers })
      .then((res) => {
        if (res.ok) return res.json();
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem('access_token');
          setIsAuthenticated(false);
        }
        throw new Error(`Failed to fetch user profile: ${res.status} ${res.statusText}`);
      })
      .then((data) => {
        if (data) {
          // Before setMe, so the render the new profile causes already shows times in
          // the zone this user reads.
          configureTimestamps({
            orgZone: data.org_primary_time_zone,
            clock: data.org_clock_format,
            mode: data.org_time_display_mode,
            userZone: data.profile?.time_zone,
          });
          setMe(data);
          // R2/R4: on successful auth, sync this device to the org's Remember
          // Email policy, then remember the email if enabled. Never a token.
          // Default Enabled (PRD R4): only an explicit `false` disables. Treats a
          // missing field (backend not yet returning it) as enabled, not disabled.
          if (data.org_remember_email_enabled !== false) {
            setRememberEmailEnabled(true);
            if (data.email) setRememberedEmail(data.email);
          } else {
            setRememberEmailEnabled(false);
            clearRememberedEmail();
            clearInflightEmail();
          }
        }
      })
      .catch((err) => {
        console.error('Error fetching user profile:', err);
      });
  }, []);

  // Fetch current user and role-based features when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchUserProfile();
  }, [isAuthenticated, fetchUserProfile]);

  // When backend returns 401 (e.g. token expired), api.js already cleared token; just update UI
  useEffect(() => {
    const handler = () => setIsAuthenticated(false);
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handler);
  }, []);

  // Re-check token when page becomes visible again (e.g. user returns after 24h idle) and log out if expired
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && clearExpiredAccessToken()) {
        setIsAuthenticated(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await apiService.logout();
    } catch (error) {
      if (error.status !== 401) {
        console.error('[App] Logout API error:', error);
      }
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('active_org_id');
    localStorage.removeItem('active_org_name');
    sessionStorage.clear();
    configureTimestamps();
    setMe(null);
    setIsAuthenticated(false);
    navigate('/login', { replace: true });
  }, [navigate]);

  // Initialize models (singleton pattern)
  const dataSourceWizardModel = useMemo(() => new DataSourceWizardModel(), []);
  const csvReviewModel = useMemo(() => new CsvReviewModel(), []);
  // One per signed-in user and active organisation (see useLibraryModel).
  const libraryModel = useLibraryModel(me);

  // Feature flags
  const enableCsvHitlReview = useFlag('enable_csv_hitl_review');
  const showAiIndexedSummary = useFlag('show-ai-indexed-summary');

  // PRD-945: dismiss the overlay locally as soon as Done completes so the user
  // doesn't see it again before the next /user-profile poll resolves.
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const handleOnboardingComplete = useCallback(() => {
    setOnboardingDismissed(true);
    navigate('/', { replace: true });
    fetchUserProfile();
  }, [navigate, fetchUserProfile]);
  const showOnboarding = !onboardingDismissed && me && me.onboarding_completed_at == null;

  // Closing the New Dataset wizard is a navigation, not just a state change:
  // it lives at `/data-sources?new=1` so Back can return to the dataset list,
  // so the param has to go with it. Defined before the ViewModel because the
  // ViewModel routes Escape and the backdrop through it too.
  //
  // REPLACE, not push. A push leaves history as
  //   [/data-sources, /data-sources?new=1, /data-sources]
  // so the very next browser Back lands on `?new=1` again, the effect below
  // sees `wizardRequested && !isOpen` and reopens the wizard blank at step 1 —
  // the user has to press Back twice to leave. That is the inverse of the fix
  // this close handler exists for. Replacing collapses the last two entries,
  // so Back goes where the reviewer expects.
  const handleCloseWizard = useCallback(() => {
    navigate('/data-sources', { replace: true });
  }, [navigate]);

  // Use ViewModels
  const dataSourceWizardViewModel = useDataSourceWizardViewModel(
    dataSourceWizardModel,
    handleCloseWizard,
  );
  const csvReviewViewModel = useCsvReviewViewModel(csvReviewModel);

  // CSV review state hoisted here so both /data-sources and /ask-resai can trigger the same modal
  const [reviewDatasetId, setReviewDatasetId] = useState(null);
  const [reviewJobId, setReviewJobId] = useState(null);
  // Keyed by csv job id, never by dataset id. Keying by dataset made this latch permanent:
  // adding a second CSV to a dataset raises a fresh review, but the dataset was already in the
  // set, so the Review button stayed hidden behind a stale "submitted" chip. Nothing could
  // approve the new review, and its document sat in ENRICHING waiting on a callback that
  // depends on that approval.
  const [submittedReviewJobs, setSubmittedReviewJobs] = useState(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem('csv_submitted_review_jobs') || '[]');
      return new Set(stored);
    } catch { return new Set(); }
  });
  useEffect(() => {
    sessionStorage.setItem('csv_submitted_review_jobs', JSON.stringify([...submittedReviewJobs]));
  }, [submittedReviewJobs]);
  // Bumped after publish / review submit / review close so subscribed dataset lists refetch
  const [datasetsRefreshKey, setDatasetsRefreshKey] = useState(0);
  const bumpDatasetsRefresh = useCallback(() => setDatasetsRefreshKey((k) => k + 1), []);

  const handleOpenReview = useCallback(async (dataset) => {
    if (!enableCsvHitlReview) return;
    // Prefer csv_job_id from the dataset list payload — backend populates it
    // whenever job_status === 'awaiting_input'. Fall back to the per-dataset
    // jobs endpoint to cover the brief race between suspend and rollup.
    let jobId = dataset.csv_job_id || null;
    if (!jobId) {
      try {
        // PRD-1387: keyed on `status === 'awaiting_input'`, never on the v1
        // `pipeline` name — see utils/csvReviewJob.js.
        jobId = selectAwaitingInputJobId(await apiService.getDatasetJobs(dataset.id));
      } catch (err) {
        console.error('[handleOpenReview] Failed to fetch jobs:', err);
        jobId = CsvReviewModel.getPersistedJobId(dataset.id);
      }
    }
    if (!jobId) {
      showNotification('Unable to find job for this dataset. Please re-index to review.', 'error');
      return;
    }
    CsvReviewModel.persistJobMapping(dataset.id, jobId, dataset.name);
    setReviewDatasetId(dataset.id);
    setReviewJobId(jobId);
    csvReviewViewModel.openReview(dataset.id, jobId);
  }, [enableCsvHitlReview, csvReviewViewModel]);

  const handleCloseReview = useCallback(() => {
    setReviewDatasetId(null);
    setReviewJobId(null);
    csvReviewViewModel.reset();
    bumpDatasetsRefresh();
  }, [csvReviewViewModel, bumpDatasetsRefresh]);

  const handleSubmitReview = useCallback(async () => {
    const outcome = await csvReviewViewModel.submitReview();

    // 422: incomplete approval — keep the modal open with columns highlighted.
    if (outcome === 'incomplete' || outcome === 'error') return;

    // 409: the job already moved on. Close quietly and let polling refresh status —
    // this is not an error to the user.
    if (outcome === 'conflict') {
      showNotification('This dataset is already processing. Refreshing status.', 'info');
    } else {
      showNotification('Review submitted successfully. Pipeline is resuming.', 'success');
      if (reviewJobId) {
        setSubmittedReviewJobs((prev) => new Set(prev).add(reviewJobId));
      }
    }

    setReviewDatasetId(null);
    setReviewJobId(null);
    csvReviewViewModel.reset();
    setTimeout(bumpDatasetsRefresh, 3000);
  }, [csvReviewViewModel, reviewJobId, bumpDatasetsRefresh]);

  const [openAutomationWizard, setOpenAutomationWizard] = useState(false);
  const currentPage = getPageIdFromPathname(location.pathname);

  // PRD-1359 (feedback doc §Creating a New Dataset): "pressing the back button
  // in my browser doesn't return me to the Data Sources/Datasets screen, I'm
  // redirected to the section I was at before navigating to Data Sources."
  //
  // The New Dataset wizard replaces the Data Sources page but used to open
  // without touching the URL, so it added no history entry — Back skipped over
  // it entirely and left for whatever route preceded Data Sources.
  //
  // The URL is now the single driver: `?new=1` means the wizard is open. Back
  // and Forward drop and restore the param, this effect follows it, and a
  // refresh or a pasted link lands on the wizard rather than a blank list.
  // Everything that closes the wizard goes through handleCloseWizard above, so
  // the param is never left behind pointing at a closed wizard.
  const wizardRequested = new URLSearchParams(location.search).get('new') === '1'
    && location.pathname === '/data-sources';
  useEffect(() => {
    if (wizardRequested && !dataSourceWizardViewModel.isOpen) {
      dataSourceWizardViewModel.openWizard(1);
    } else if (!wizardRequested && dataSourceWizardViewModel.isOpen) {
      dataSourceWizardViewModel.closeWizard();
    }
  }, [wizardRequested, dataSourceWizardViewModel]);



  // Navigate by page id (Sidebar still uses page ids)
  const handleNavigate = useCallback((pageId) => {
    const route = APP_ROUTES.find((r) => r.pageId === pageId);
    if (route) navigate(route.path);
  }, [navigate]);

  // When unauthenticated, ensure URL is /login
  useEffect(() => {
    if (isAuthenticated) return;
    if (location.pathname !== '/login') navigate('/login', { replace: true });
  }, [isAuthenticated, location.pathname, navigate]);

  const [initialReportQuery, setInitialReportQuery] = useState('');
  const [initialReportResponse, setInitialReportResponse] = useState('');
  const [initialReportThreadId, setInitialReportThreadId] = useState('');
  const [initialReportDatasetIds, setInitialReportDatasetIds] = useState([]);

  // Report email deep-link (/automations?automation=…&run=…): open that run's
  // delivered report in the run-history drawer (PRD-1269).
  const [runViewAutomationId, setRunViewAutomationId] = useState('');
  const [runViewRunId, setRunViewRunId] = useState('');
  useEffect(() => {
    if (location.pathname !== '/automations') return;
    const params = new URLSearchParams(location.search);
    const a = params.get('automation');
    if (a) {
      setRunViewAutomationId(a);
      setRunViewRunId(params.get('run') || '');
    }
  }, [location.pathname, location.search]);

  // Notification state
  const [notification, setNotification] = useState({
    isVisible: false,
    message: '',
    type: 'success'
  });

  const handleScheduleReportFromChat = (payload) => {
    const isObj = payload && typeof payload === 'object' && 'queryText' in payload;
    const queryText = isObj ? (payload.queryText ?? '') : (typeof payload === 'string' ? payload : '');
    const normalized =
      typeof queryText === 'string'
        ? queryText.trim()
        : Array.isArray(queryText)
          ? queryText.map((p) => (p?.text ?? p?.content ?? '')).filter(Boolean).join(' ').trim()
          : '';
    const responsePreview = isObj && typeof payload.responsePreview === 'string' ? payload.responsePreview.trim() : '';
    const threadId = isObj && payload.threadId != null ? String(payload.threadId) : '';
    const datasetIds = isObj && Array.isArray(payload.datasetIds) ? payload.datasetIds : [];
    setInitialReportQuery(normalized);
    setInitialReportResponse(responsePreview);
    setInitialReportThreadId(threadId);
    setInitialReportDatasetIds(datasetIds);
    navigate('/automations');
    setOpenAutomationWizard(true);
  };


  const handleNavigateToCreate = () => {
    // Pushes a history entry, which is what gives Back something to return to.
    navigate('/data-sources?new=1');
  };

  // `duration` (ms) is optional: 0 keeps the notification up until it is closed,
  // for a message that lists things to fix (PRD-1628). Omitted, it closes after 5s.
  const showNotification = (message, type = 'success', { duration } = {}) => {
    setNotification({
      isVisible: true,
      message,
      type,
      duration
    });
  };

  const hideNotification = () => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  };

  // Global poll for HITL review notifications (works from any page)
  const reviewNotifiedJobsRef = useRef(new Set());
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;

  useEffect(() => {
    if (!isAuthenticated || !enableCsvHitlReview) return;

    const checkForReviews = async () => {
      try {
        const entries = CsvReviewModel.getPersistedMappings();
        if (entries.length === 0) return;

        for (const { datasetId, jobId, name } of entries) {
          try {
            const status = await apiService.getJobStatus(jobId);
            if (status.status === 'awaiting_input') {
              if (!reviewNotifiedJobsRef.current.has(jobId)) {
                reviewNotifiedJobsRef.current.add(jobId);
                const label = name || 'A';
                showNotificationRef.current(`${label} dataset requires your review before processing can continue`, 'info');
              }
            } else if (status.status === 'completed' || status.status === 'failed') {
              CsvReviewModel.clearPersistedJob(datasetId);
              reviewNotifiedJobsRef.current.delete(jobId);
            } else {
              // Status is 'processing' — clear the notified flag so we notify again
              // if it transitions back to awaiting_input (e.g. columns after definitions)
              reviewNotifiedJobsRef.current.delete(jobId);
            }
          } catch (jobErr) {
            if (jobErr.status === 404) {
              CsvReviewModel.clearPersistedJob(datasetId);
              reviewNotifiedJobsRef.current.delete(jobId);
            }
          }
        }
      } catch (e) {
        // Silently ignore poll errors
      }
    };

    checkForReviews();
    const interval = setInterval(checkForReviews, 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated, enableCsvHitlReview]);

  const features = me?.features || {};

  // Check if user has access to a route (explicit feature flag from API takes priority over defaults)
  const hasRouteAccess = useCallback((route, f) => {
    // Insights is also behind its Unleash kill-switch. Without this, a user shut out
    // of Ask ResAI is sent here as their first accessible page, the flag sends them
    // back to "/", and the router loops until React gives up.
    if (route.pageId === 'insights' && flagsReady && !insightsWidgetsEnabled) return false;
    if (route.pageId === 'library') return libraryPageEnabled;
    if (route.feature && f[route.feature] !== undefined) return f[route.feature];
    // No backend feature key exists yet for context authoring — super-admins
    // reach it by URL when the Unleash flag is on (matches the route gating).
    if (route.pageId === 'context-authoring') return contextPromotionEnabled && !!me?.is_super_admin;
    return route.featureDefault || false;
  }, [contextPromotionEnabled, me, flagsReady, insightsWidgetsEnabled, libraryPageEnabled]);

  // Find the first page the user has access to
  const getFirstAccessiblePath = useCallback((f) => {
    for (const route of APP_ROUTES) {
      if (hasRouteAccess(route, f)) return route.path;
    }
    return '/';
  }, [hasRouteAccess]);

  // Redirect when user has no access to current route (after me loaded)
  useEffect(() => {
    if (!me) return;
    // Wait for a flag decision (ready or failed); a failed fetch never sets ready.
    if ((currentPage === 'context-authoring' || currentPage === 'library') && !flagsReady && !flagsError) return;
    const currentRoute = APP_ROUTES.find((r) => r.pageId === currentPage);
    if (!currentRoute) return;
    if (!hasRouteAccess(currentRoute, features)) {
      navigate(getFirstAccessiblePath(features), { replace: true });
    }
  }, [me, currentPage, features, navigate, getFirstAccessiblePath, hasRouteAccess, flagsReady, flagsError]);

  if (!isAuthenticated) {
    return <Login backupLoginEnabled={backupLoginEnabled} />;
  }

  // PRD-945: while first-login onboarding is pending, render ONLY the overlay
  // (no sidebar, no routes, no background fetches). Once the user clicks Done,
  // showOnboarding flips false and the full app shell mounts normally.
  if (showOnboarding) {
    return (
      <div className="app">
        <OnboardingOverlay
          onComplete={handleOnboardingComplete}
          onShowNotification={showNotification}
        />
        <Notification
          message={notification.message}
          type={notification.type}
          isVisible={notification.isVisible}
          onClose={hideNotification}
          duration={notification.duration}
        />
      </div>
    );
  }

  return (
    <div className="app">
      {/* PRD-1418 page shell: the skip link is the first focusable element and
          jumps past the nav rail to the main region. */}
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <Sidebar
        onNavigate={handleNavigate}
        currentPage={currentPage}
        onLogout={handleLogout}
        features={features}
        activeOrgName={localStorage.getItem('active_org_name')}
        onClearOrgSwitch={() => {
          localStorage.removeItem('active_org_id');
          localStorage.removeItem('active_org_name');
          fetchUserProfile();
        }}
      />
      <main className="app-main" id="main-content" tabIndex={-1}>
        <OrgTimeZonePrompt me={me} settingsEnabled={enabled} />
        <Routes>
          <Route path="/" element={<><AppHeader pageTitle="Ask ResAI" user={me} onRefreshUser={fetchUserProfile} onShowNotification={showNotification} onLogout={handleLogout} className="app-header--borderless" /><AskResAI
              user={me}
              onScheduleReport={handleScheduleReportFromChat}
              onShowNotification={showNotification}
              enableCsvHitlReview={enableCsvHitlReview}
              submittedReviewJobs={submittedReviewJobs}
              onOpenReview={handleOpenReview}
              datasetsRefreshKey={datasetsRefreshKey}
              onPublishSuccess={bumpDatasetsRefresh}
              features={features}
            /></>} />
          {/* Blank until flags resolve (or fail) so a deep link neither flashes the page nor
              bounces early. When the kill-switch is OFF, leave for the first page this user
              may see — never a fixed "/", which loops for a user shut out of Ask ResAI. */}
          <Route path="/insights" element={
            (!flagsReady && !flagsError) ? null
              : !insightsWidgetsEnabled ? <Navigate to={getFirstAccessiblePath(features)} replace />
              : <><AppHeader pageTitle="Insights" user={me} onRefreshUser={fetchUserProfile} onShowNotification={showNotification} onLogout={handleLogout} /><Insights user={me} onShowNotification={showNotification} /></>
          } />
          {/* Legacy alias — existing deep links and bookmarks land on the new home. */}
          <Route path="/ask-resai" element={<Navigate to="/" replace />} />
          <Route path="/data-sources" element={
            <><AppHeader pageTitle="Data Sources" user={me} onRefreshUser={fetchUserProfile} onShowNotification={showNotification} onLogout={handleLogout} />
            <DataSources
              onNavigateToCreate={handleNavigateToCreate}
              onShowNotification={showNotification}
              features={features}
              enableCsvHitlReview={enableCsvHitlReview}
              showAiIndexedSummary={showAiIndexedSummary}
              wizardIsOpen={dataSourceWizardViewModel.isOpen}
              submittedReviewJobs={submittedReviewJobs}
              onOpenReview={handleOpenReview}
              datasetsRefreshKey={datasetsRefreshKey}
            /></>
          } />
          <Route path="/automations" element={<><AppHeader pageTitle="Automations" user={me} onRefreshUser={fetchUserProfile} onShowNotification={showNotification} onLogout={handleLogout} /><Automations onShowNotification={showNotification} openWizard={openAutomationWizard} onWizardClose={() => {
            setOpenAutomationWizard(false);
            setInitialReportQuery('');
            setInitialReportResponse('');
            setInitialReportThreadId('');
            setInitialReportDatasetIds([]);
          }}
          initialReportQuery={initialReportQuery}
          initialReportResponse={initialReportResponse}
          initialReportThreadId={initialReportThreadId}
          initialReportDatasetIds={initialReportDatasetIds}
          initialRunAutomationId={runViewAutomationId}
          initialRunId={runViewRunId}
          onRunViewConsumed={() => {
            setRunViewAutomationId('');
            setRunViewRunId('');
            navigate('/automations', { replace: true });
          }}
           /></>} />
          <Route path="/push-to-bi" element={<><AppHeader pageTitle="Push to BI" user={me} onRefreshUser={fetchUserProfile} onShowNotification={showNotification} onLogout={handleLogout} /><PushToBI onShowNotification={showNotification} /></>} />
          <Route path="/activity-log" element={<><AppHeader pageTitle="Activity Log" user={me} onRefreshUser={fetchUserProfile} onShowNotification={showNotification} onLogout={handleLogout} /><ActivityLog /></>} />
          <Route path="/audit-logs" element={<><AppHeader pageTitle="Audit Logs" user={me} onRefreshUser={fetchUserProfile} onShowNotification={showNotification} onLogout={handleLogout} /><AuditLogs /></>} />
          {enabled ? (
            <Route path="/settings" element={<><AppHeader pageTitle="Settings" user={me} onRefreshUser={fetchUserProfile} onShowNotification={showNotification} onLogout={handleLogout} /><Settings onShowNotification={showNotification} onLogout={handleLogout} me={me} onRefreshMe={fetchUserProfile} /></>} />
          ) : (
            <Route path="/settings" element={<><AppHeader pageTitle="Settings" user={me} onRefreshUser={fetchUserProfile} onShowNotification={showNotification} onLogout={handleLogout} /><p>Settings are not enabled</p></>} />
          )}
          {/* Flag off / not super-admin: no route at all — the unknown-URL fallback takes over
              instead of advertising a disabled feature (review suggestion). */}
          {contextPromotionEnabled && me?.is_super_admin && (
            <Route path="/context-authoring" element={<><AppHeader pageTitle="Context Authoring" user={me} onRefreshUser={fetchUserProfile} onShowNotification={showNotification} onLogout={handleLogout} /><ContextAuthoring /></>} />
          )}
          {/* PRD-1605: blank until flags resolve, as for Insights, so a deep link neither
              flashes the catch-all Ask ResAI nor bounces early. The page is unshipped, so
              a flag-service failure closes it rather than opening it. */}
          <Route path="/library" element={
            (!flagsReady && !flagsError) ? null
              : !libraryPageEnabled ? <Navigate to={getFirstAccessiblePath(features)} replace />
              : <><AppHeader pageTitle="Library" user={me} onRefreshUser={fetchUserProfile} onShowNotification={showNotification} onLogout={handleLogout} /><Library model={libraryModel} onShowNotification={showNotification} /></>
          } />
          {/* Any unknown URL falls back to Ask ResAI — the home page. */}
          <Route path="*" element={<><AppHeader pageTitle="Ask ResAI" user={me} onRefreshUser={fetchUserProfile} onShowNotification={showNotification} onLogout={handleLogout} className="app-header--borderless" /><AskResAI
              user={me}
              onScheduleReport={handleScheduleReportFromChat}
              onShowNotification={showNotification}
              enableCsvHitlReview={enableCsvHitlReview}
              submittedReviewJobs={submittedReviewJobs}
              onOpenReview={handleOpenReview}
              datasetsRefreshKey={datasetsRefreshKey}
              onPublishSuccess={bumpDatasetsRefresh}
              features={features}
            /></>} />
        </Routes>
        {enableCsvHitlReview && reviewDatasetId && csvReviewViewModel.phase && csvReviewViewModel.phase !== 'idle' && (
          <CsvReviewModal
            phase={csvReviewViewModel.phase}
            reviewPayload={csvReviewViewModel.reviewPayload}
            definitionSource={csvReviewViewModel.definitionSource}
            suggestionsFailed={csvReviewViewModel.suggestionsFailed}
            editedDefinitions={csvReviewViewModel.editedDefinitions}
            invalidColumns={csvReviewViewModel.invalidColumns}
            error={csvReviewViewModel.error}
            onSubmitReview={handleSubmitReview}
            onUpdateDefinition={csvReviewViewModel.updateDefinition}
            onUpdateDefinitionField={csvReviewViewModel.updateDefinitionField}
            onClose={handleCloseReview}
          />
        )}
        {dataSourceWizardViewModel.isOpen && (
          <DataSourceWizard
            isOpen={dataSourceWizardViewModel.isOpen}
            currentStep={dataSourceWizardViewModel.currentStep}
            totalSteps={dataSourceWizardViewModel.totalSteps}
            datasetId={dataSourceWizardViewModel.datasetId}
            datasetIds={dataSourceWizardViewModel.datasetIds}
            formData={dataSourceWizardViewModel.formData}
            selectedFiles={dataSourceWizardViewModel.selectedFiles}
            promptValidated={dataSourceWizardViewModel.promptValidated}
            validationFeedback={dataSourceWizardViewModel.validationFeedback}
            refinementNotes={dataSourceWizardViewModel.refinementNotes}
            onClose={handleCloseWizard}
            onNext={dataSourceWizardViewModel.nextStep}
            onBack={dataSourceWizardViewModel.previousStep}
            onBackdropClick={(e) => {
              if (e.target === e.currentTarget) handleCloseWizard();
            }}
            onCreateDataset={dataSourceWizardViewModel.createDataset}
            onUploadFiles={dataSourceWizardViewModel.uploadFiles}
            onExtractFields={dataSourceWizardViewModel.extractFields}
            onRefinePrompt={dataSourceWizardViewModel.validatePrompt}
            onSetFormData={dataSourceWizardViewModel.setFormData}
            onSetSelectedFiles={dataSourceWizardViewModel.setSelectedFiles}
            onRefinementNotesChange={dataSourceWizardViewModel.setRefinementNotes}
            onClearFormData={dataSourceWizardViewModel.clearFormData}
            onPublishDataset={dataSourceWizardViewModel.publishDataset}
            onSaveAsDraft={dataSourceWizardViewModel.saveAsDraft}
            onShowNotification={showNotification}
            onSetDatasetIds={dataSourceWizardViewModel.setDatasetIds}
          />
        )}
      </main>

      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={hideNotification}
        duration={notification.duration}
      />
    </div>
  );
}

export default App;
